import { Skeleton, Tooltip } from '@mui/material';
import { ReactNode } from 'react';

import {
  IconTrendingDownMS,
  IconTrendingFlatMS,
  IconTrendingUpMS,
} from '../../../../constants/icons';
import { Card, DeltaRow, TitleRow, Value } from './styles';

export type DeltaDirection = 'higher-is-good' | 'lower-is-good';

interface KpiCardProps {
  label: string;
  value: string;
  hint?: string;
  delta: number | null;
  direction: DeltaDirection;
  previousLabel: string;
  loading?: boolean;
  icon?: ReactNode;
}

const formatDelta = (n: number): string => {
  const abs = Math.abs(n);
  return `${n >= 0 ? '↑' : '↓'} ${abs}%`;
};

const toneFor = (
  delta: number,
  direction: DeltaDirection
): 'good' | 'bad' | 'neutral' => {
  if (delta === 0) return 'neutral';
  if (direction === 'higher-is-good') return delta > 0 ? 'good' : 'bad';
  return delta < 0 ? 'good' : 'bad';
};

const trendIconFor = (delta: number): JSX.Element => {
  if (delta > 0) return IconTrendingUpMS;
  if (delta < 0) return IconTrendingDownMS;
  return IconTrendingFlatMS;
};

export function KpiCard({
  label,
  value,
  hint,
  delta,
  direction,
  previousLabel,
  loading,
  icon,
}: KpiCardProps): JSX.Element {
  return (
    <Card>
      <TitleRow>
        <Tooltip title={hint || ''} arrow>
          <span>{label}</span>
        </Tooltip>
        {icon}
      </TitleRow>
      {loading ? (
        <Skeleton variant="text" width="60%" sx={{ fontSize: '32px' }} />
      ) : (
        <Value>{value}</Value>
      )}
      {loading && <Skeleton variant="text" width="40%" />}
      {!loading && delta === null && (
        <DeltaRow tone="neutral">
          {IconTrendingFlatMS}— vs {previousLabel}
        </DeltaRow>
      )}
      {!loading && delta !== null && (
        <DeltaRow tone={toneFor(delta, direction)}>
          {trendIconFor(delta)}
          {formatDelta(delta)} vs {previousLabel}
        </DeltaRow>
      )}
    </Card>
  );
}
